import { Injectable } from '@angular/core';
import { PipelineStageConfig } from '../interfaces/biozoom-api/pipeline';
import { OperatorFunction } from 'rxjs';
import { ScanHeartObservablesService } from './scanHeart/scan-heart-observables.service';
import { ScanGenericSinglesService } from './scanGeneric/scan-generic-singles';
import { ScanNutritionSinglesService } from './scanNutrition/scan-nutrition-singles';
import { GenericStageService } from './genericStages/generic-stages.service';
import { delay, first, timeout } from 'rxjs/operators';
import { FileManagerService } from './fileManager/file-manager.service';
import { UsbApiService } from './scanner/usb-api.service';

@Injectable({
  providedIn: 'root'
})
export class GenConfigService {
  constructor(
    private genericSinglesObservable: GenericStageService,
    private scanHeartObservableService: ScanHeartObservablesService,
    private scanGenericSinglesObservable: ScanGenericSinglesService,
    private scanNutritionSinglesObservable: ScanNutritionSinglesService,
    private helperService: GenericStageService,
    private fileManagerService: FileManagerService,
    private usbApiService: UsbApiService,
  ) { }

  /**
   * convert an array of desired steps into a pipeline that can be subscribed
   * @param step steps the pipeline should contain
   * @param args config object, containing callback methods needed by the scan observables
   * @returns pipeline observable that contains a single measurement step
   */
  parsePipelineConfig = (
    step: string,
    args: PipelineStageConfig,
  ) => {
    args.stage = args.getStep().step;
    const pipeline: OperatorFunction<any, any>[] = [];
    let isStage = true;
    // for (const step of steps) {
    switch (step) {
      case 'if': {
        pipeline.push(this.helperService.checkIf(args));
        pipeline.push(first());
        break;
      }
      case 'checkObjectKeys': {
        pipeline.push(this.helperService.checkObjectKeys(args));
        pipeline.push(first());
        break;
      }
      case 'switch': {
        pipeline.push(this.helperService.switchFunc(args));
        pipeline.push(first());
        break;
      }
      case 'copyValues': {
        pipeline.push(this.helperService.copyValues(args));
        pipeline.push(first());
        break;
      }
      case 'setValues': {
        pipeline.push(this.helperService.setValues(args));
        pipeline.push(first());
        break;
      }
      case 'writeLog': {
        pipeline.push(this.helperService.writeLog(args));
        pipeline.push(first());
        break;
      }
      case 'splitString': {
        pipeline.push(this.helperService.splitString(args));
        pipeline.push(first());
        break;
      }
      case 'pushToResultArray': {
        pipeline.push(this.fileManagerService.pushToResultArray(args));
        pipeline.push(first());
        break;
      }
      case 'exportResults': {
        pipeline.push(this.fileManagerService.export(args));
        pipeline.push(first());
        break;
      }
      case 'deleteResults': {
        pipeline.push(this.fileManagerService.deleteMeasurements(args));
        pipeline.push(first());
        break;
      }
      case 'checkInternet': {
        pipeline.push(this.helperService.checkInternetConnection(args));
        pipeline.push(first());
        break;
      }
      case 'scanQRScanner': {
        pipeline.push(this.genericSinglesObservable.scanQRScanner(args));
        pipeline.push(first());
        break;
      }
      case 'fetchScannerWifi': {
        pipeline.push(this.genericSinglesObservable.fetchWifiName(args));
        pipeline.push(first());
        break;
      }
      case 'validateScanner': {
        pipeline.push(this.genericSinglesObservable.validateScanner(args));
        pipeline.push(first());
        break;
      }
      case 'checkFirmware': {
        pipeline.push(this.genericSinglesObservable.checkFirmware(args));
        pipeline.push(first());
        break;
      }
      case 'updateFirmware': {
        pipeline.push(this.genericSinglesObservable.updateFirmware(args, 'espressif'));
        pipeline.push(first());
        break;
      }
      case 'updateFirmwareUSB': {
        pipeline.push(this.genericSinglesObservable.updateFirmware(args, 'espressif'));
        pipeline.push(first());
        break;
      }
      case 'downgradeFirmwareUSB': {
        pipeline.push(this.genericSinglesObservable.updateFirmware(args, 'espressif', false, true));
        pipeline.push(first());
        break;
      }
      case 'updateInfineonUSB': {
        pipeline.push(this.genericSinglesObservable.updateFirmware(args, 'infineon', false));
        pipeline.push(first());
        break;
      }
      case 'updateInfineonBootloaderUSB': {
        pipeline.push(this.genericSinglesObservable.updateFirmware(args, 'infineon', true));
        pipeline.push(first());
        break;
      }
      case 'finalizeScanner': {
        pipeline.push(this.scanGenericSinglesObservable.finalizeScanner(args));
        pipeline.push(first());
        break;
      }
      case 'clearScannerFlash': {
        pipeline.push(this.scanGenericSinglesObservable.deleteFlash(args));
        pipeline.push(first());
        break;
      }
      case 'resetAll': {
        pipeline.push(this.scanGenericSinglesObservable.resetAll(args, false));
        pipeline.push(first());
        break;
      }
      case 'resetAllStage': {
        pipeline.push(this.scanGenericSinglesObservable.resetAll(args, true));
        pipeline.push(first());
        break;
      }
      case 'selectAOXPrecise': {
        pipeline.push(this.scanNutritionSinglesObservable.selectAOXPrecise(args));
        pipeline.push(first());
        break;
      }
      case 'connectScanner': {
        pipeline.push(this.scanGenericSinglesObservable.connectScanner(args));
        pipeline.push(first());
        break;
      }
      case 'getScannerFirmware': {
        pipeline.push(this.scanGenericSinglesObservable.checkScannerFirmware(args));
        pipeline.push(first());
        break;
      }
      case 'handOnRecognition': {
        pipeline.push(this.scanGenericSinglesObservable.handOnRecognition(args));
        pipeline.push(first());
        break;
      }
      case 'measureAOX': {
        pipeline.push(this.scanNutritionSinglesObservable.startMeasurement(args));
        pipeline.push(first());
        break;
      }
      case 'measureAOXHalf': {
        pipeline.push(this.scanNutritionSinglesObservable.startMeasurement(args, 'front_half'));
        pipeline.push(first());
        break;
      }
      case 'measureAOXSplit': {
        pipeline.push(this.scanNutritionSinglesObservable.startMeasurement(args, 'split'));
        pipeline.push(first());
        break;
      }
      case 'measureHeart': {
        pipeline.push(this.scanHeartObservableService.measureHeart(args));
        pipeline.push(first());
        break;
      }
      case 'initMeasureHeart': {
        pipeline.push(this.scanHeartObservableService.initMeasurement(args));
        pipeline.push(first());
        break;
      }
      case 'getMeasurementHeart': {
        pipeline.push(this.scanHeartObservableService.fetchMeasurement(args));
        pipeline.push(first());
        break;
      }
      case 'measureHeartTest': {
        pipeline.push(this.scanHeartObservableService.startMeasurementTest(args));
        pipeline.push(first());
        break;
      }
      case 'handOffRecognition': {
        pipeline.push(this.scanGenericSinglesObservable.handOffRecognition(args));
        pipeline.push(first());
        break;
      }
      case 'repeatMeasurement': {
        pipeline.push(this.scanGenericSinglesObservable.repeatMeasurement(args));
        pipeline.push(first());
        break;
      }
      case 'connectToServer': {
        pipeline.push(this.scanGenericSinglesObservable.connectToServer(args));
        pipeline.push(first());
        break;
      }
      case 'uploadDataAOX': {
        // Legacy config function. New configs should use the transparent AOX upload pipeline.
        pipeline.push(this.scanNutritionSinglesObservable.uploadData(args));
        pipeline.push(first());
        break;
      }
      case 'preRequestAOX': {
        pipeline.push(this.scanNutritionSinglesObservable.preRequestAox(args));
        pipeline.push(first());
        break;
      }
      case 'uploadRawDataAOX': {
        pipeline.push(this.scanNutritionSinglesObservable.uploadRawDataAox(args));
        pipeline.push(first());
        break;
      }
      case 'processUploadResultAOX': {
        pipeline.push(this.scanNutritionSinglesObservable.processUploadResultAox(args));
        pipeline.push(first());
        break;
      }
      // These names are retained for config compatibility. The pipeline also handles
      // single AOX and HRV/Vitality results; a future breaking rename could use
      // prepareCombinedMeasurementResult, uploadCombinedMeasurementResult, and processCombinedMeasurementResult.
      case 'preRequestPreciseAOX': {
        pipeline.push(this.scanGenericSinglesObservable.preRequestPreciseAox(args));
        pipeline.push(first());
        break;
      }
      case 'uploadRawDataPreciseAOX': {
        pipeline.push(this.scanGenericSinglesObservable.uploadRawDataPreciseAox(args));
        pipeline.push(first());
        break;
      }
      case 'processUploadResultPreciseAOX': {
        pipeline.push(this.scanGenericSinglesObservable.processUploadResultPreciseAox(args));
        pipeline.push(first());
        break;
      }
      case 'uploadDataHeart': {
        // Legacy config function. New configs should use the transparent Heart upload pipeline.
        pipeline.push(this.scanHeartObservableService.uploadData(args));
        pipeline.push(first());
        break;
      }
      case 'uploadRawDataHeart': {
        pipeline.push(this.scanHeartObservableService.uploadRawDataHeart(args));
        pipeline.push(first());
        break;
      }
      case 'processUploadResultHeart': {
        pipeline.push(this.scanHeartObservableService.processUploadResultHeart(args));
        pipeline.push(first());
        break;
      }
      case 'uploadDataHeartTest': {
        // Legacy config function. New configs should use uploadDataHeartTestWithStatus.
        pipeline.push(this.scanHeartObservableService.uploadDataTest(args));
        pipeline.push(first());
        break;
      }
      case 'uploadDataHeartTestWithStatus': {
        pipeline.push(this.scanHeartObservableService.uploadDataTestWithStatus(args));
        pipeline.push(first());
        break;
      }
      case 'repeatUpload': {
        pipeline.push(this.scanGenericSinglesObservable.repeatUpload(args));
        pipeline.push(first());
        break;
      }
      case 'deleteResult': {
        pipeline.push(this.scanGenericSinglesObservable.deleteResult(args));
        pipeline.push(first());
        break;
      }
      case 'fetchResult': {
        pipeline.push(this.scanGenericSinglesObservable.fetchResult(args));
        pipeline.push(first());
        break;
      }
      case 'openURL': {
        pipeline.push(this.helperService.openURL(args));
        break;
      }
      case 'displayResults': {
        pipeline.push(this.scanGenericSinglesObservable.displayResults(args));
        break;
      }
      case 'genResultURL': {
        pipeline.push(this.helperService.genResultURL(args));
        break;
      }
      case 'genResultTable': {
        pipeline.push(this.helperService.genResultTable(args));
        break;
      }
      case 'scanQRGeneric': {
        pipeline.push(this.genericSinglesObservable.scanQRGeneric(args));
        pipeline.push(first());
        break;
      }
      case 'goToHome': {
        pipeline.push(this.scanGenericSinglesObservable.goToHome(args));
        break;
      }
      case 'refresh': {
        pipeline.push(this.helperService.refresh(args));
        pipeline.push(first());
        break;
      }
      case 'updateApp': {
        pipeline.push(this.helperService.updateApp(args));
        pipeline.push(first());
        break;
      }
      case 'getRequest': {
        pipeline.push(this.helperService.get(args));
        pipeline.push(first());
        break;
      }
      case 'postRequest': {
        pipeline.push(this.helperService.post(args));
        pipeline.push(first());
        break;
      }
      case 'downloadMedia': {
        pipeline.push(this.helperService.download(args));
        pipeline.push(first());
        break;
      }
      case 'getConfig': {
        pipeline.push(this.helperService.getConfig(args));
        pipeline.push(first());
        break;
      }
      case 'logout': {
        pipeline.push(this.helperService.logout(args));
        pipeline.push(first());
        break;
      }
      case 'stashMeasurement': {
        pipeline.push(this.fileManagerService.saveMeasurement(args));
        pipeline.push(first());
        break;
      }
      case 'popMeasurement': {
        pipeline.push(this.fileManagerService.uploadMeasurement(args));
        pipeline.push(first());
        break;
      }
      case 'cleanupMeasurement': {
        pipeline.push(this.fileManagerService.deleteMeasurement(args));
        pipeline.push(first());
        break;
      }
      case 'parseJSON': {
        pipeline.push(this.helperService.parseJSON(args));
        pipeline.push(first());
        break;
      }
      case 'hashString': {
        pipeline.push(this.helperService.hashString(args));
        pipeline.push(first());
        break;
      }
      case 'print': {
        pipeline.push(this.helperService.print(args));
        pipeline.push(first());
        break;
      }
      case 'deleteSerial': {
        pipeline.push(this.helperService.deleteSerial(args));
        pipeline.push(first());
        break;
      }
      case 'getUSBSerial': {
        pipeline.push(this.scanGenericSinglesObservable.fetchSerial(args));
        pipeline.push(first());
        break;
      }
      case 'validateCredits': {
        pipeline.push(this.helperService.validateCredits(args));
        pipeline.push(first());
        break;
      }
      case 'checkScannerSoftwareVersion': {
        pipeline.push(this.genericSinglesObservable.checkScannerSoftwareVersion(args));
        pipeline.push(first());
        break;
      }
      case 'saveResultsFilteredForExport': {
        pipeline.push(this.fileManagerService.saveResultsFilteredForExport(args));
        pipeline.push(first());
        break;
      }
      case 'exportResultsFiltered': {
        pipeline.push(this.fileManagerService.exportResultsFiltered(args));
        pipeline.push(first());
        break;
      }
      case 'sendGenericCommand': {
        pipeline.push(this.genericSinglesObservable.sendGenericCommand(args));
        pipeline.push(first());
        break;
      }
      case 'compareResultToReferenceScanners': {
        pipeline.push(this.scanGenericSinglesObservable.compareResultToReferenceScanners(args));
        pipeline.push(first());
        break;
      }
      case 'getMostRelevantMeasurementErrors': {
        pipeline.push(this.genericSinglesObservable.getMostRelevantMeasurementErrors(args));
        pipeline.push(first());
        break;
      }
      case 'getMostRelevantSeriesMeasurementErrors': {
        pipeline.push(this.genericSinglesObservable.getMostRelevantSeriesMeasurementErrors(args));
        pipeline.push(first());
        break;
      }


      default: {
        isStage = false;
        break;
      }
    }
    return { pipeline, isStage };
  }

  /**
   * create a timeout pipeline
   * @param args config object, containing callback methods needed by the scan observables
   * @param timeoutDuration the maximum time allowed between each value the stage emits
   * @param onExpiration stage to go to once timeout occurs
   * @returns pipeline observable that contains a guaranteed timeout
   */
  parsePipelineTimeout = (
    args: PipelineStageConfig,
    timeoutDuration?: number,
    onExpiration?: string,
  ) => {
    if (!timeoutDuration) {
      return [];
    }
    const pipeline: OperatorFunction<any, any>[] = [];
    pipeline.push(delay(timeoutDuration * 2));
    pipeline.push(timeout(timeoutDuration));
    pipeline.push(this.scanGenericSinglesObservable.handleError(args, onExpiration ?? ''));
    return pipeline;
  }

  errorWrapper = (args: PipelineStageConfig) => {
    return this.scanGenericSinglesObservable.handleError(args);
  }
}
